import './../../Player.css';
import React, {useContext, useEffect, useState, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerContext } from '../../PlayerContext';
import { openFullscreen, browserLocales, unescapeHtml } from '../../Utils';
import { analyticsManager } from '../../../index';
import SecondaryButton, {promoType} from '../components/SecondaryButton';
import FadeInPanel from '../components/FadeInPanel';
import { useAnimate } from 'framer-motion';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getStoredPixelConsent } from '../../PixelConsentOverlay';
import { hasChainRestaurants } from '../../chainRestaurantUtils';

function PromoButton(props){
    const { t } = useTranslation();
    const [isOpened, setIsOpened] = useState(false);

    const storyDimensions = useContext(PlayerContext).get("storyDimensions");
    const propagateFocusChange = useContext(PlayerContext).get("propagateFocusChange");
    const pauseVideo = useContext(PlayerContext).get("pauseVideo");
    const setActiveFeature = useContext(PlayerContext).get("setActiveFeature");
    const activeFeature = useContext(PlayerContext).get("activeFeature");
    const appAnalyticsManager = useContext(PlayerContext).get("appAnalyticsManager");
    const playMode = useContext(PlayerContext).get("playMode");
    const sizeMultiplier = useContext(PlayerContext).get("sizeMultiplier");
    const setPlayAudio = useContext(PlayerContext).get("setPlayAudio");
    const audioAlreadyDisabled = useContext(PlayerContext).get("audioAlreadyDisabled");
    const playAudio = useContext(PlayerContext).get("playAudio");
    const playerOnDesktop = useContext(PlayerContext).get("playerOnDesktop");
    const fromMaker = useContext(PlayerContext).get("fromMaker");
    const fromDemo = useContext(PlayerContext).get("fromDemo");
    const highlight = useContext(PlayerContext).get("highlight");
    const idPrefix = fromDemo ? (fromDemo +"-") : ""; 

    const [, animate] = useAnimate();
    const transformComponentRef = useRef(null);

    useEffect(() => {
        if (highlight != null){

            let elementId = null;
            
            if (highlight.includes("promo")){
                elementId = "secondary-btn-promo";
            }            

            const element = document.getElementById(elementId);
            if (elementId != null && element != null){                   
                const animation = async () => {
                    await animate(element, { background: "#FFFFFFFF"}, {duration: 0.6});
                    if (element != null)
                        animate(element, { background: "#FFFFFF00"}, {duration: 0.6});
                }
                
                animation();
            }
        }
      }, [highlight, animate]);

    const panelRootStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: storyDimensions.containerWidth,
        height: storyDimensions.containerHeight,
        pointerEvents: 'auto',
        zIndex: 20,
    };

    if (props.story.posId == null && !hasChainRestaurants(props.story)) return (null);
        
    const open = () => {          
        if (!fromMaker /*&& !fromDemo*/ && !playerOnDesktop && !audioAlreadyDisabled && !playAudio){                        
            const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
            if (ambiantPlayer && ambiantPlayer.paused){
                try {
                    ambiantPlayer.play();                
                } catch(error){}
            }
            setPlayAudio(true);            
        }
        pauseVideo();
        openFullscreen();
        setActiveFeature("promo");
        setIsOpened(true);
      
        analyticsManager.logCheckPromoTag(props.storyId, playMode, true);
                
        if (appAnalyticsManager)
            appAnalyticsManager.clickedPromotions();

        if (getStoredPixelConsent() === 'accepted') {
            if (window.ttq){
                window.ttq.track('ViewContent', {                
                    "contents": [
                        {              
                            "content_id": "0",           
                            "content_name": "promo",
                        }
                    ],
                });
            }
            if (window.fbq){                    
                window.fbq('track', 'ViewContent', {"content_name": "promo"});
            }
        }
    };

    const close = () => {
        propagateFocusChange(true);
        setIsOpened(false);
        setActiveFeature(null);
        analyticsManager.logCheckPromoTag(props.storyId, playMode, false);
    };
    
    function showCloseButton(){
        return (
            <div style={{
                position: 'absolute',
                bottom: 16 * sizeMultiplier,
                left: 0,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 40
            }}>
                
                <IconButton
                    onClick={close}
                    style={{
                        background: "#fff",
                        borderRadius: "50%",                                    
                        padding: 4 * sizeMultiplier,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}                        
                >                                     
                    <CloseIcon style={{ color: "#808080", fontSize: 24 * sizeMultiplier, width: 28 * sizeMultiplier, height: 28 * sizeMultiplier,}} />                        
                </IconButton>
            </div>
        );                    
    }
    
    if (props.show){                  
        if (isOpened && activeFeature === "promo"){             
            // Détecter la langue actuelle
            const language = browserLocales(true)[0] === "fr" ? "fr" : "en";
            const currentPromoType = props.story.promoType;
            const promoImg = props.story.promoImg?.[language] || props.story.promoImg?.fr || props.story.promoImg?.en || null;
            
            if (currentPromoType === "img" && promoImg) {
                // Lien unique (indépendant de la langue) ; compat. ancien format { fr, en }
                const promoImgLinkRaw = typeof props.story.promoImgLink === 'string'
                    ? props.story.promoImgLink
                    : (props.story.promoImgLink?.fr || props.story.promoImgLink?.en || "");
                const promoImgLink = typeof promoImgLinkRaw === 'string' ? promoImgLinkRaw.trim() : "";
                const closeButtonSpace = 56 * sizeMultiplier;
                const offerBarHeight = promoImgLink ? 44 * sizeMultiplier : 0;
                const maxImgHeight = Math.max(0, storyDimensions.containerHeight - closeButtonSpace - offerBarHeight - 16 * sizeMultiplier);
                const maxImgWidth = storyDimensions.containerWidth;
                const promoColor = props.story.primaryColor || '#2FC4B5';

                return (
                    <FadeInPanel
                        className="promo-container"
                        animationKey="promo-img"
                        style={panelRootStyle}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            id={idPrefix + "gallery-overlay"}
                            className="BlockingOverlay"
                            style={{
                                width: storyDimensions.containerWidth,
                                height: storyDimensions.containerHeight,
                                background: "#FFFFFFD0",
                                pointerEvents: 'auto',
                            }}
                            onClick={close}
                        />
                        
                        <div
                            className="FullScreenContainer"
                            style={{
                                backgroundColor: 'transparent',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                paddingBottom: closeButtonSpace,
                            }}
                            onClick={(e)=>{e.stopPropagation();}}
                        >
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                width: '100%',
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    width: '100%',
                                }}>
                                    <TransformWrapper ref={transformComponentRef}>
                                        <TransformComponent>
                                            <img
                                                alt={t('PromoAlt')}
                                                src={promoImg}
                                                style={{
                                                    objectFit: "contain",
                                                    maxWidth: maxImgWidth,
                                                    maxHeight: maxImgHeight,
                                                    width: 'auto',
                                                    height: 'auto',
                                                    display: 'block',
                                                }}
                                            />
                                        </TransformComponent>
                                    </TransformWrapper>
                                </div>
                                {promoImgLink && (
                                    <a
                                        href={promoImgLink}
                                        target="_blank"
                                        rel="noopener"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            height: offerBarHeight,
                                            backgroundColor: '#FFFFFF',
                                            borderTop: `1px solid ${promoColor}33`,
                                            borderBottom: `1px solid ${promoColor}33`,
                                            fontSize: 16 * sizeMultiplier,
                                            fontWeight: 600,
                                            fontFamily: 'Roboto, sans-serif',
                                            color: promoColor,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        {t('GetTheOffer')}
                                    </a>
                                )}
                            </div>
                        </div>
                        {showCloseButton()}
                    </FadeInPanel>
                );
            }
            
            // Si le type est "text" ou par défaut, afficher l'UI actuelle
            const sanitizeHtml = (html) => {
                if (!html) return "";
                return html
                    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                    .replace(/\n/g, '<br>')
                    .replace(/(<br\s*\/?>\s*)+$/gi, '');
            };

            const promoTitleRaw = typeof props.story.promoTitle === 'object'
                ? (props.story.promoTitle?.[language] || props.story.promoTitle?.fr || props.story.promoTitle?.en || "")
                : (props.story.promoTitle || "");
            const promoDescriptionRaw = typeof props.story.promoDescription === 'object'
                ? (props.story.promoDescription?.[language] || props.story.promoDescription?.fr || props.story.promoDescription?.en || "")
                : (props.story.promoDescription || "");

            const promoTitle = sanitizeHtml(unescapeHtml(promoTitleRaw));
            const promoDescription = sanitizeHtml(unescapeHtml(promoDescriptionRaw));
            const restaurantAvatar = props.story.avatarUrl;
            const promoColor = props.story.primaryColor;
            const bandHeight = 30 * sizeMultiplier;
            const avatarSize = 3 * bandHeight;
            const cardRadius = 8 * sizeMultiplier;

            return (
                <FadeInPanel
                    className="promo-container"
                    animationKey="promo-text"
                    style={panelRootStyle}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        id={idPrefix + "gallery-overlay"}
                        className="BlockingOverlay"
                        style={{
                            width: storyDimensions.containerWidth,
                            height: storyDimensions.containerHeight,
                            background: "#FFFFFFD0",
                            pointerEvents: 'auto',
                        }}
                        onClick={close}
                    />
                    
                    <div style={{
                        zIndex: 10,
                        position: 'absolute',
                        top: '50%',
                        left: 16 * sizeMultiplier,
                        right: 16 * sizeMultiplier,
                        transform: 'translateY(-50%)',
                        backgroundColor: '#FFFFFF',
                        borderRadius: cardRadius,
                        paddingBottom: 16 * sizeMultiplier,
                        maxHeight: storyDimensions.containerHeight * 0.7,
                        overflow: 'visible',
                        display: 'flex',
                        flexDirection: 'column',
                    }} onClick={(e)=>{e.stopPropagation();}}>
                        <div style={{
                            position: 'relative',
                            height: bandHeight,
                            backgroundColor: promoColor,
                            borderTopLeftRadius: cardRadius,
                            borderTopRightRadius: cardRadius,
                            flexShrink: 0,
                        }}>
                            {restaurantAvatar && (
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: 0,
                                    transform: 'translate(-50%, -50%)',
                                    width: avatarSize,
                                    height: avatarSize,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    zIndex: 2,
                                }}>
                                    <img
                                        alt={t('RestaurantAvatarAlt')}
                                        src={restaurantAvatar}
                                        className="FBAvatarContainer"
                                        onError={(e) => { e.target.style.display = "none"; }}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{
                            overflowY: 'auto',
                            flex: 1,
                            minHeight: 0,
                        }}>
                            <div style={{
                                paddingLeft: 16 * sizeMultiplier,
                                paddingRight: 16 * sizeMultiplier,
                                paddingTop: restaurantAvatar
                                    ? avatarSize / 2 - bandHeight + 16 * sizeMultiplier
                                    : 16 * sizeMultiplier,
                            }}>
                                {promoTitle && (
                                    <div
                                        className="card-html-content"
                                        style={{
                                            marginBottom: 16 * sizeMultiplier,
                                            fontSize: 18 * sizeMultiplier,
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            textAlign: 'center',
                                            fontFamily: 'Roboto, sans-serif',
                                            lineHeight: 1.3,
                                        }}
                                        dangerouslySetInnerHTML={{__html: promoTitle}}
                                    />
                                )}

                                {promoDescription && (
                                    <div
                                        className="card-html-content"
                                        style={{
                                            paddingTop: 8 * sizeMultiplier,
                                            paddingBottom: 8 * sizeMultiplier,
                                            fontSize: 14 * sizeMultiplier,
                                            fontWeight: 'normal',
                                            color: '#666666',
                                            lineHeight: 1.4,
                                            fontFamily: 'Roboto, sans-serif',
                                            textAlign: 'left',
                                            userSelect: 'text',
                                            WebkitUserSelect: 'text',
                                            MozUserSelect: 'text',
                                            msUserSelect: 'text',
                                        }}
                                        dangerouslySetInnerHTML={{__html: promoDescription}}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {showCloseButton()}
                </FadeInPanel>
            );
        }
        else {                
            if (props.story.promoType != null){                           
                return (
                    <SecondaryButton buttonType={promoType} story={props.story} color={props.story.primaryColor} open={open}/>
                );
            }
            else {                
                return null;
            }
        }                
    }
    else {        
        return null;
    }
}    

export default PromoButton;